import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext from './AuthContext';
import { parseTokensFromUrl, decryptTokenPayload, isEncryptedToken, sanitizeUrl, getExpirationEpoch, persistSession, loadSession, clearSession, persistNotificationParams, validateJwtStructure } from './tokenUtils';

const TOKEN_SKEW_SEC = 30;

function getUserFromIdToken(idToken) {
  if (!idToken) return null;
  try {
    const [, payload] = idToken.split('.');
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json || null;
  } catch (e) {
    return null;
  }
}

function scheduleTimer(ms, cb) {
  const id = setTimeout(cb, ms);
  return () => clearTimeout(id);
}

export default function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [scope, setScope] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const user = useMemo(() => getUserFromIdToken(idToken), [idToken]);
  const refreshCancelRef = useRef(null);

  const clearTimers = () => {
    if (refreshCancelRef.current) {
      refreshCancelRef.current();
      refreshCancelRef.current = null;
    }
  };

  const startExpiryTimer = useCallback((expEpochSec) => {
    clearTimers();
    const nowSec = Math.floor(Date.now() / 1000);
    const leadSec = Math.max(0, expEpochSec - TOKEN_SKEW_SEC - nowSec);
    refreshCancelRef.current = scheduleTimer(leadSec * 1000, () => {
      try {
        if (window.__WEBVIEW_TOKEN_PROVIDER__) {
          const maybePromise = window.__WEBVIEW_TOKEN_PROVIDER__();
          Promise.resolve(maybePromise)
            .then((t) => {
              if (!t) return;
              const at = t.access_token || null;
              const it = t.id_token || null;
              const sc = t.scope || '';
              const newExp = getExpirationEpoch(t);
              setAccessToken(at);
              setIdToken(it);
              setScope(sc);
              setExpiresAt(newExp);
              startExpiryTimer(newExp);
              setIsAuthenticated(!!(at || it));
              persistSession({ access_token: at, id_token: it, scope: sc, expires_at: newExp });
            })
            .catch(() => setIsAuthenticated(false));
        } else if (window?.chrome?.webview?.postMessage) {
          window.chrome.webview.postMessage({ type: 'TOKEN_EXPIRED' });
        } else if (window?.webkit?.messageHandlers?.native) {
          window.webkit.messageHandlers.native.postMessage('TOKEN_EXPIRED');
        } else {
          setIsAuthenticated(false);
        }
      } catch (_) {
        setIsAuthenticated(false);
      }
    });
  }, []);

  const bootstrapFromUrl = useCallback(async () => {
    const { hasTokens, params } = parseTokensFromUrl();
    if (!hasTokens) return false;

    try {
      let at = params.access_token || null;
      const it = params.id_token || null;
      const sc = params.scope || '';

      // If access_token is encrypted (no dots = not a JWT), decrypt it
      if (at && isEncryptedToken(at)) {
        const decrypted = await decryptTokenPayload(at);
        if (!decrypted) {
          console.warn('Token bootstrap: decryption of access_token failed');
          clearSession();
          setIsTokenInvalid(true);
          setIsAuthenticated(false);
          sanitizeUrl();
          return false;
        }
        // Decrypted value is the plain JWT
        at = decrypted;
      }

      const tokenToValidate = at || it;
      if (tokenToValidate && !validateJwtStructure(tokenToValidate)) {
        console.warn('Token bootstrap: malformed JWT structure');
        clearSession();
        setIsTokenInvalid(true);
        setIsAuthenticated(false);
        sanitizeUrl();
        return false;
      }

      const exp = getExpirationEpoch({ ...params, access_token: at });
      const nowSec = Math.floor(Date.now() / 1000);
      if (exp <= nowSec) {
        console.warn('Token bootstrap: token is expired');
        setIsTokenInvalid(true);
        setIsAuthenticated(false);
        sanitizeUrl();
        return false;
      }
      setAccessToken(at);
      setIdToken(it);
      setScope(sc);
      setExpiresAt(exp);
      setIsAuthenticated(!!(at || it));
      startExpiryTimer(exp);
      persistSession({ access_token: at, id_token: it, scope: sc, expires_at: exp });
      persistNotificationParams({ llId: params.llId, trainingId: params.trainingId });
      sanitizeUrl();
      return true;
    } catch (e) {
      console.error('Token bootstrap failed', e);
      sanitizeUrl();
      clearSession();
      setIsAuthenticated(false);
      setIsTokenInvalid(true);
      return false;
    }
  }, [startExpiryTimer]);

  const logout = useCallback(() => {
    clearTimers();
    setAccessToken(null);
    setIdToken(null);
    setScope('');
    setExpiresAt(0);
    setIsAuthenticated(false);
    clearSession();
  }, []);

  const getAccessToken = useCallback(() => accessToken, [accessToken]);

  useEffect(() => {
    const fromSession = loadSession(TOKEN_SKEW_SEC);
    if (fromSession.ok) {
      setAccessToken(fromSession.access_token || null);
      setIdToken(fromSession.id_token || null);
      setScope(fromSession.scope || '');
      setExpiresAt(fromSession.expires_at);
      setIsAuthenticated(!!(fromSession.access_token || fromSession.id_token));
      startExpiryTimer(fromSession.expires_at);
    }
    bootstrapFromUrl().then((bootstrapped) => {
      if (!bootstrapped && !fromSession.ok) {
        setIsTokenInvalid(true);
      }
      setIsBootstrapping(false);
    });
  }, [bootstrapFromUrl, startExpiryTimer]);

  const value = useMemo(() => ({
    isAuthenticated,
    isTokenInvalid,
    isBootstrapping,
    user,
    accessToken,
    idToken,
    scope,
    expiresAt,
    logout,
    getAccessToken,
  }), [isAuthenticated, isTokenInvalid, isBootstrapping, user, accessToken, idToken, scope, expiresAt, logout, getAccessToken]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
